import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

import './AndroidOssLicenses.scss';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator.tsx';
import {
  OssVersionFetchResponse,
  VersionRouteParams,
  genereateFetchLibraryLicenseContent,
  OssLibraryLicenseResponse,
} from '@moment/net/version-fetch-query.ts';

// import { generateFetchOssLibraryLicenseQuery, OssLibraryLicenseResponse } from '@moment/net/oss-library-license-fetch-query.ts'; // We'll create this

export interface AndroidOssLicensesProps {
  // queryFn for fetching the list of libraries for a version
  queryFn: (options: VersionRouteParams) => Promise<OssVersionFetchResponse>;
}

// A simple modal component
const LicenseModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  isLoading: boolean;
  isError: boolean;
}> = ({ isOpen, onClose, title, content, isLoading, isError }) => {
  if (!isOpen) return null;

  return (
    <div className="android-oss-modal-overlay" onClick={onClose}>
      <div className="android-oss-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="android-oss-modal-close-button" onClick={onClose}>
          &times;
        </button>
        <h2>{title}</h2>
        {isLoading && <div className="android-oss-modal-status"><ActivityIndicator /> Loading license...</div>}
        {isError && <div className="android-oss-modal-status android-oss-modal-error">Error loading license.</div>}
        {!isLoading && !isError && (
          <textarea className="android-oss-modal-license-text" readOnly value={content} />
        )}
      </div>
    </div>
  );
};

export const AndroidOssLicensesLayout: React.FC<AndroidOssLicensesProps> = ({ queryFn }) => {
  const params = useParams<VersionRouteParams>();
  const queryClient = useQueryClient(); // For prefetching or manual query management if needed

  const [selectedLibrary, setSelectedLibrary] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Query for the list of libraries
  const {
    data: librariesData,
    isLoading: isLoadingLibraries,
    isError: isErrorLibraries,
  } = useQuery<OssVersionFetchResponse, Error, OssVersionFetchResponse>({
    queryKey: ['ossLibraries', params.version], // Include version in queryKey
    queryFn: () => {
      if (!params.version) {
        return Promise.reject(new Error("Version parameter is missing"));
      }
      return queryFn(params as VersionRouteParams)(); // Cast params
    },
    enabled: !!params.version, // Only run query if version is present
  });

  // Query for the specific license content when a library is selected
  const {
    data: licenseContentData,
    isLoading: isLoadingLicenseContent,
    isError: isErrorLicenseContent,
    refetch: fetchLicenseContent, // We'll call this manually
  } = useQuery<OssLibraryLicenseResponse, Error, OssLibraryLicenseResponse>({
    queryKey: ['ossLicenseContent', params.version, selectedLibrary],
    queryFn: () => {
      if (!params.version || !selectedLibrary) {
        return Promise.reject(new Error("Version or library name is missing for license fetch"));
      }
      // Assuming generateFetchOssLibraryLicenseQuery takes version and libraryName
      return genereateFetchLibraryLicenseContent(params.version, selectedLibrary)();
    },
    enabled: false, // Initially disabled, will be enabled and triggered by selecting a library
    retry: false, // Optional: prevent retries if a license isn't found immediately
  });

  const handleLibraryClick = (libraryName: string) => {
    setSelectedLibrary(libraryName);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (isModalOpen && selectedLibrary && params.version) {
      // When modal opens for a selected library, fetch its content
      fetchLicenseContent();
    }
  }, [isModalOpen, selectedLibrary, params.version, fetchLicenseContent]);


  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLibrary(null);
  };

  if (isLoadingLibraries) {
    return (
      <div className="android-oss-status-overlay android-oss-loading">
        <ActivityIndicator />
        Loading libraries...
      </div>
    );
  }

  if (isErrorLibraries) {
    return (
      <div className="android-oss-status-overlay android-oss-error">
        Error loading library list. Please check the version and try again.
      </div>
    );
  }

  if (!librariesData || librariesData.length === 0) {
    return (
      <div className="android-oss-status-overlay">
        No open source libraries found for this version.
      </div>
    );
  }

  console.log(`licenseContentData = ${JSON.stringify(licenseContentData)}`)

  return (
    <div className="android-oss-container">
      <ul className="android-oss-library-list">
        {librariesData.map((item) => (
          <li
            key={item.libraryName}
            className="android-oss-library-item"
            onClick={() => handleLibraryClick(item.libraryName)}
          >
            {item.libraryName}
            <span className="android-oss-library-item-chevron">&gt;</span>
          </li>
        ))}
      </ul>
      <LicenseModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedLibrary || 'License'}
        content={licenseContentData?.find((item) => { return item.libraryName === selectedLibrary })?.license?.licenseContent || ''}
        isLoading={isLoadingLicenseContent}
        isError={isErrorLicenseContent}
      />
    </div>
  );
};
